import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { important } from '../utils/global/helper';
import '../components/calendar/calendar.css';
import useCheckoutForm from '../hooks/global/useCheckoutForm';
import PageLayout from '../components/global/PageLayout';
import useSubscriptionModal from '../hooks/subscriptions/useSubscriptionModal';
import useFeatureGuard from '../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../types/subscriptions';
import Error from '../components/global/Error';
import { createRows, headerCells } from '../utils/table/paymentsTable';
import ReuseableTable from '../components/table/ReuseableTable';
import useCheckoutSocket from '../hooks/payments/useCheckoutSocket';
import useGetGroupedPayments from '../hooks/payments/useGetGroupedPayments';
import { CheckoutProvider } from '../context/CheckoutContext';
import { PAYMENT_GROUPS_PAGE_SIZE, SORTABLE_KEYS } from '../constants/payments';
import useSort from '../hooks/global/useSort';
import PaymentsSideBarModal from '../components/payments/PaymentsSideBarModal';
import useCheckoutState from '../hooks/checkouts/useCheckoutState';
import useCheckPendingPayment from '../hooks/payments/useCheckPendingPayment';
import { invalidateDepositTransferAppointments } from '../services/appointments';
import { getCheckoutErrorMessage } from '../helpers/payments';
import useInSplitCheckout from '../hooks/payments/useInSplitCheckout';

export function CheckoutPageComponent() {
  const { openSubscriptionModal } = useSubscriptionModal();
  const canCheckout = useFeatureGuard(Feature.CHECKOUT);

  const {
    selectedPaymentsGroup,
    setSelectedPaymentsGroup,
    pagination: { page, set, hasNext, hasPrevious },
  } = useCheckoutState();

  const sort = useSort(SORTABLE_KEYS, {
    defaultSort: {
      orderBy: 'createdAt',
      order: 'desc',
    },
    onSort: () => {
      set(1);
    },
  });
  const {
    data: groupedPaymentsResult,
    refetch,
    isError,
    isLoading,
  } = useGetGroupedPayments({
    page,
    sort,
    selectedPaymentsGroup,
    setSelectedPaymentsGroup,
  });
  const { totalCount } = groupedPaymentsResult || {};

  const {
    openCheckout,
    terminalCheckoutModal,
    resetTerminalCheckoutModalState,
    setTerminalCheckoutModalToSuccess,
    setCheckoutFailureData,
    setTerminalCheckoutModalToFailed,
    mostRecentPaymentId,
  } = useCheckoutForm();

  React.useEffect(() => {
    if (!canCheckout) {
      openSubscriptionModal();
    }
  }, [canCheckout, openSubscriptionModal]);

  const error = isError ? 'Error loading orders' : '';

  // Create sets of table rows/lists for each tab
  const rows = createRows({
    items: groupedPaymentsResult?.groups,
  });

  const handleRowClick = (groupKey: string) => {
    const row = groupedPaymentsResult?.groups.find(
      group => group.groupKey === groupKey,
    );
    if (!row) return;
    setSelectedPaymentsGroup(row);
  };

  const isInSplitCheckout = useInSplitCheckout();

  useCheckPendingPayment({
    enabled: !isInSplitCheckout,
  });
  useCheckoutSocket({
    enabled: !isInSplitCheckout,
    onCheckoutCompleted: async data => {
      if (
        !data.paymentId ||
        (data.paymentId !== mostRecentPaymentId &&
          !selectedPaymentsGroup?.payments.some(p => p.id === data.paymentId))
      )
        return;
      refetch();

      if (data.appointmentId) {
        invalidateDepositTransferAppointments();
      }

      if (terminalCheckoutModal.status === 'loading') {
        setTerminalCheckoutModalToSuccess();
      } else {
        resetTerminalCheckoutModalState();
      }
    },
    onCheckoutFailed: async data => {
      if (
        !data.paymentId ||
        (data.paymentId !== mostRecentPaymentId &&
          !selectedPaymentsGroup?.payments.some(p => p.id === data.paymentId))
      )
        return;

      setCheckoutFailureData(
        data as Parameters<typeof setCheckoutFailureData>[0],
      );
      const tempFailureMessage = getCheckoutErrorMessage(data);

      setTerminalCheckoutModalToFailed(tempFailureMessage);
      refetch();
    },
  });

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 200px)',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Error errorMessage={error} />
      </Box>
    );
  }

  return (
    <PageLayout title="Checkouts">
      <Box
        className="header-right"
        sx={{
          display: {
            xs: 'inline',
            md: 'none',
          },
        }}>
        <Button
          variant="contained"
          className="primary hidden-desktop"
          onClick={() => openCheckout()}>
          New Checkout
        </Button>
      </Box>
      <div className="calendar-header">
        <Box
          className="header-right"
          sx={{
            display: {
              xs: important('none'),
              md: important('inline'),
            },
          }}>
          <Button
            variant="contained"
            className="primary"
            onClick={() => openCheckout()}>
            New Checkout
          </Button>
        </Box>
      </div>

      <ReuseableTable
        name="Checkouts"
        rows={rows}
        headerCells={headerCells}
        emptyMessage="No checkouts to show right now. If you believe this is an error, please reach out to our support team."
        loading={isLoading}
        handleRowClick={handleRowClick}
        pagination={{
          count: totalCount ?? PAYMENT_GROUPS_PAGE_SIZE,
          rowsPerPage: PAYMENT_GROUPS_PAGE_SIZE,
          colSpan: 3,
          page: page - 1,
          setPage: newPage => set(newPage + 1),
          hasNext,
          hasPrevious,
        }}
        sort={sort}
      />

      <PaymentsSideBarModal
        paymentsGroup={selectedPaymentsGroup}
        onClose={() => setSelectedPaymentsGroup(null)}
        onRefresh={() => {
          refetch();
        }}
      />
    </PageLayout>
  );
}

export default function CheckoutPage() {
  return (
    <CheckoutProvider>
      <CheckoutPageComponent />
    </CheckoutProvider>
  );
}
